import {ReactElement, useEffect, useState} from "react";
import {Property} from "../../contexts/properties/logic";
import {utsvOpMetricQueryReturn} from "../../pages/workflows/operational/utils/operational-metric-helpers";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import Intro from "./steps/Intro";
import * as css from "./styles/revenue-wizard.module.scss";
import {ImMagicWand} from "react-icons/im";
import {ReactComponent as ChevronLeft} from '@zendeskgarden/svg-icons/src/16/chevron-left-fill.svg';
import {ReactComponent as ChevronRight} from '@zendeskgarden/svg-icons/src/16/chevron-right-fill.svg';
import OccupancyAndRenewals from "./steps/OccupancyAndRenewals";
import {useBatchUpsertUnitTypeScheduleValuesMutation, useCalculateCurveExpirationsLazyQuery, VersionType} from "../../__generated__/generated_types";
import ExpirationCurve from "./steps/ExpirationCurves";
import CloseIcon from '@material-ui/icons/Close';
import useRevenueWizard, {IUnitType, IUnitTypeMetrics} from "./logic/useRevenueWizard";
import {adjustValuesTo100} from "../../pages/bulk-assumptions/BulkAssumptionMathHelpers";
import SummaryTable from "./components/SummaryTable";
import {CheckCircleRounded} from "@material-ui/icons";
import {Field, Input, Label} from "@zendeskgarden/react-forms";
import {useVersions} from "../../contexts/versions/VersionsContext";
import {toast} from "react-toastify";
import {Inline} from "@zendeskgarden/react-loaders";


interface IMetricSaveObject {
    metricKey: keyof IUnitTypeMetrics;
    dbFieldName: string;
    toRate?: (count: number, data: IUnitTypeMetrics, unitType: IUnitType, monthIndex: number) => number;
}

interface IRevenueWizardProps {
    property: Property;
    budgetYear: number;
    isOpen: boolean;
    metricData: utsvOpMetricQueryReturn;
    onClose: (dataSaved: boolean) => void;
}

export default function RevenueWizard({property, budgetYear, isOpen, metricData, onClose}: IRevenueWizardProps): ReactElement {
    // TODO @bowman this is used to track if we have any more steps for the next button to go to, but
    // im sure there is a better way to handle it besides this hard coded number
    const lastStepIndex = 4;
    const {versions} = useVersions();
    const rfcstVersionId = versions?.valueVersions.find(x => x.year == budgetYear - 1 && x.type == VersionType.Reforecast)?.id;
    const bdgtVersionId = versions?.valueVersions.find(x => x.year == budgetYear && x.type == VersionType.Budget)?.id;

    // ui state stored directly in this component
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
    const [confirmApplyVisible, setConfirmApplyVisible] = useState(false);
    const [confirmationText, setConfirmationText] = useState<string>("");

    // wizard data state and util functions live in useRevenueWizard, and this instance is passed to sub-components
    // so they can all share the same state and access the utility functions
    const revenueWizardInstance = useRevenueWizard(metricData, property, budgetYear);
    const {
        unitTypeData,
        rfcstExpirations,
        bdgtExpirations,
        isFormDirty,
        selectedMetricToFavor,
        setRfcstExpirations,
        setBdgtExpirations,
        setCorrectedBdgtExpirations,
        initialize,
        resetData,
        countToRate,
        convertValuesWithFavor,
    } = revenueWizardInstance;

    const [calculateCorrectedExpirations, {data: correctedExpirationsData, loading: correctedExpirationsDataLoading}] = useCalculateCurveExpirationsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [batchUpsertUnitTypeScheduleValues, {loading: savingValues}] = useBatchUpsertUnitTypeScheduleValuesMutation({
        onCompleted(data) {
            if (data?.batchUpsertUnitTypeScheduleValues) {
                resetAndClose(true);
                toast.success("Values applied!");
            } else {
                toast.error("There was a problem saving, please refresh and try again.");
            }
        },
    });

    // when modal opens, the wizard setup script calculates expiration curves and stores initial form state for dirty checks
    useEffect(() => {
        if (metricData && isOpen) {
            initialize();
        }
    }, [metricData, isOpen]);

    // our expiration curve for the bdgt season needs to use corrected values to ensure 100%
    useEffect(() => {
        if (!correctedExpirationsDataLoading && correctedExpirationsData?.calculateExpirationCurveExpirations) {
            const unitTypeBdgtExpirationMap = new Map();

            for (const unitType of correctedExpirationsData?.calculateExpirationCurveExpirations) {
                unitTypeBdgtExpirationMap.set(unitType.unitTypeId, unitType.budgetExpirations);
            }

            setCorrectedBdgtExpirations(unitTypeBdgtExpirationMap);
        }
    }, [correctedExpirationsData, correctedExpirationsDataLoading]);

    useEffect(() => {
        if (confirmApplyVisible === false) {
            setConfirmationText("");
        }
    }, [confirmApplyVisible]);

    useEffect(() => {
        if (confirmCloseVisible === true) {
            setConfirmApplyVisible(false);
        }
    }, [confirmCloseVisible]);

    function applyValues() {
        if (!rfcstVersionId || !bdgtVersionId || savingValues) {
            return;
        }

        // array to define each metric we want to save, what field it maps to in the database, and the method to translate counts to rates
        const metricsToSave: IMetricSaveObject[] = [
            {metricKey: "expirationCount", dbFieldName: "leaseExpirationCount"},
            {metricKey: "occupancyCount", dbFieldName: "occupancyRate", toRate: (count: number, _data: IUnitTypeMetrics, unitType: IUnitType, _monthIndex: number) => countToRate(count, unitType.unitCount)},
            {metricKey: "renewalCount", dbFieldName: "renewalRatio", toRate: (count: number, data: IUnitTypeMetrics, _unitType: IUnitType, monthIndex: number) => countToRate(count, data.expirationCount[monthIndex] ?? 0)},
            {metricKey: "moveOutCount", dbFieldName: "moveOutRate", toRate: (count: number, data: IUnitTypeMetrics, _unitType: IUnitType, monthIndex: number) => countToRate(count, data.expirationCount[monthIndex] ?? 0)},
        ];

        const payloads = [];
        const unitTypeDataFavoringAMetric = convertValuesWithFavor(unitTypeData, selectedMetricToFavor);

        for (const unitType of unitTypeDataFavoringAMetric) {
            for (const metric of metricsToSave) {
                // we want to skip actuals months for the rfcst version
                const versionData = [
                    {id: rfcstVersionId, data: unitType.rfcst, startIndex: property.reforecastStartMonthIndex},
                    {id: bdgtVersionId, data: unitType.bdgt, startIndex: 0},
                ];

                for (const version of versionData) {
                    const metricValues: number[] = version.data[metric.metricKey];

                    if (!metricValues) {
                        // if a metric isn't in our object, something went really wrong
                        return;
                    }

                    // slice on our array to clear out actuals
                    const monthValuesToSave = metricValues.slice(version.startIndex);

                    payloads.push({
                        fieldName: metric.dbFieldName,
                        propertyId: property.id,
                        budgetYear,
                        versionId: version.id,
                        values: monthValuesToSave.map((count, i) => {
                            const monthIndex = i + version.startIndex;
                            let value = metric.toRate ? metric.toRate(count, version.data, unitType, monthIndex) : count;

                            // we want to convert rates from things like 88.6% -> .886
                            if (metric.metricKey != "expirationCount") {
                                value = value / 100.0;
                            }

                            return {
                                unitTypeName: unitType.name,
                                monthIndex,
                                value: String(value),
                            };
                        }),
                    });
                }
            }
        }

        batchUpsertUnitTypeScheduleValues({
            variables: {batchArgs: payloads},
        });
    }

    // based on value of currentStep, render the corresponding component
    function renderCurrentStep(): ReactElement {
        switch (currentStep) {
            case 0:
                return <Intro propertyName={property.name} budgetYear={budgetYear} />;
            case 1:
                return <ExpirationCurve
                    versionType={VersionType.Reforecast}
                    property={property}
                    budgetYear={budgetYear}
                    expirations={rfcstExpirations}
                    onChange={setRfcstExpirations}
                />;
            case 2:
                return <ExpirationCurve
                    versionType={VersionType.Budget}
                    property={property}
                    budgetYear={budgetYear}
                    expirations={bdgtExpirations}
                    onChange={setBdgtExpirations}
                />;
            case 3:
                return <OccupancyAndRenewals
                    revenueWizardInstance={revenueWizardInstance}
                />;
            case 4:
                return <SummaryTable
                    revenueWizardInstance={revenueWizardInstance}
                />;
        }
        return <></>;
    }

    // function to bring back and display error text if a step doesnt pass validation
    // right now, the only hard requirement is that budget expirations total 100%
    function getStepBlockerText(): string | null {
        switch (currentStep) {
            case 2:
                if (!bdgtExpirations || Math.round(bdgtExpirations.sum()) != 100) {
                    return "Budget Expirations must add up to 100%";
                }
                return null;
        }
        return null;
    }

    function goPrevStep() {
        if (currentStep <= 0) {
            return;
        }
        setCurrentStep(currentStep - 1);
    }

    function goNextStep() {
        if (currentStep >= lastStepIndex) {
            return;
        }
        // after moving on from the bdgt expirations step, we need to hit the api method that returns corrected expirations for each unit type
        if (currentStep == 2 && bdgtExpirations) {
            const adjustedCurve = adjustValuesTo100(bdgtExpirations).map(x => String(x));
            calculateCorrectedExpirations({
                variables: {
                    propertyId: property.id,
                    budgetYear: budgetYear,
                    curve: adjustedCurve,
                }
            });
        }
        setCurrentStep(currentStep + 1);
    }

    function resetAndClose(dataSaved: boolean) {
        setCurrentStep(0);
        resetData();
        setConfirmCloseVisible(false);
        setConfirmApplyVisible(false);
        onClose(dataSaved);
    }

    if (!isOpen || !metricData) {
        return <></>;
    }

    return (
        <Modal
            className={`${css.revenueWizard} step${currentStep}`}
            isLarge
            onClose={() => isFormDirty ? setConfirmCloseVisible(true) : resetAndClose(false)}
        >
            <Header><ImMagicWand className={css.headerIcon} />Revenue Wizard</Header>
            <Body>
                <div key={currentStep} className={css.stepContainer}>
                    {renderCurrentStep()}
                </div>

                {/* TODO wizDialog is something I was trying in order to avoid having multiple nested dialogs, but it's still kind
                // of a nested dialog, so not sure if I want to keep a brand new UI element like this that isn't part of our UI kit */}
                <div className={`${css.wizDialog} ${confirmCloseVisible && css.isVisible}`}>
                    <div className={css.wizDialogInner}>
                        <h4>Closing the Wizard will discard any changes you've made.</h4>
                        <h5>Exit Wizard and discard changes?</h5>

                        <div className={css.wizDialogButtons}>
                            <Button isBasic onClick={() => setConfirmCloseVisible(false)} aria-label="return to revenue wizard">
                                <Button.StartIcon><ChevronLeft /></Button.StartIcon>
                                Return
                            </Button>
                            <Button isPrimary onClick={() => resetAndClose(false)} isDanger aria-label="discard changes and exit revenue wizard">
                                <Button.StartIcon><CloseIcon /></Button.StartIcon>
                                Exit Wizard
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={`${css.wizDialog} ${confirmApplyVisible && css.isVisible}`}>
                    <div className={css.wizDialogInner}>
                        <h4>Applying values will overwrite all metrics in your Reforecast {budgetYear - 1} and Budget {budgetYear} seasons for {property.name}.</h4>
                        <h4>The values shown in the summary table will be your new operational metrics.</h4>

                        <Field>
                            <Label>Enter "apply values" to proceed</Label>
                            <Input value={confirmationText} onChange={evt => setConfirmationText(evt.target.value)} placeholder="apply values" disabled={savingValues} />
                        </Field>

                        <div className={css.wizDialogButtons}>
                            <Button isBasic onClick={() => setConfirmApplyVisible(false)} aria-label="return to revenue wizard" disabled={savingValues}>
                                <Button.StartIcon><ChevronLeft /></Button.StartIcon>
                                Return
                            </Button>
                            {savingValues &&
                                <Inline className={css.loadingSpinner} size={24} />
                            }
                            <Button
                                isPrimary
                                onClick={applyValues}
                                aria-label="apply all revenue wizard values"
                                disabled={confirmationText !== "apply values" || savingValues}
                            >
                                <Button.StartIcon><CheckCircleRounded /></Button.StartIcon>
                                Apply All Values
                            </Button>
                        </div>
                    </div>
                </div>
            </Body>
            <Footer className={css.footer}>
                <FooterItem>
                    {/* FooterItem is rendered all the time, even when Button is not, to always keep the Next button right justified */}
                    {currentStep > 0 &&
                        <Button onClick={goPrevStep} isPrimary aria-label="navigate to previous step">
                            <Button.StartIcon><ChevronLeft /></Button.StartIcon>
                            Previous
                        </Button>
                    }
                </FooterItem>
                <FooterItem className={css.footerItem}>
                    <h5 className={css.stepBlockerText}>{getStepBlockerText()}</h5>
                    {currentStep == lastStepIndex ?
                        <Button onClick={() => setConfirmApplyVisible(true)} isPrimary disabled={getStepBlockerText() != null} aria-label="navigate to next step">
                            Apply Values
                            <Button.EndIcon><CheckCircleRounded /></Button.EndIcon>
                        </Button>
                        :
                        <Button onClick={goNextStep} isPrimary disabled={getStepBlockerText() != null} aria-label="navigate to next step">
                            Next
                            <Button.EndIcon><ChevronRight /></Button.EndIcon>
                        </Button>
                    }
                </FooterItem>
            </Footer>
            <Close aria-label="Close Revenue Wizard" />
        </Modal>
    );
}
